export type ContentBlock =
  | { type: "p"; text: string }
  | { type: "h2"; text: string }
  | { type: "h3"; text: string }
  | { type: "ul"; items: string[] }
  | { type: "code"; code: string }
  | { type: "callout"; text: string };

export type BlogPost = {
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  date: string;
  readTime: string;
  gradient: string;
  content: ContentBlock[];
};

export const blogPosts: BlogPost[] = [
  {
    slug: "introducing-rayloop",
    title: "Introducing Rayloop: Build AI Content Pipelines Without Code",
    excerpt:
      "Why do you have to be a developer to chain AI models together? We built Rayloop to fix that — a visual workflow builder for everyone creating content with AI.",
    category: "Product",
    date: "Feb 28, 2026",
    readTime: "5 min read",
    gradient: "from-amber-500/20 to-orange-500/10",
    content: [
      {
        type: "p",
        text: "At its core, Rayloop is a question we kept asking ourselves: why do you have to be a developer to chain AI models together?",
      },
      {
        type: "p",
        text: "Every content creator, marketer, and video producer we talked to was using AI tools — but in isolation. They'd prompt ChatGPT for a script, paste it into a text-to-image tool, download the images, upload them to a video generator, add voiceover manually. Each step disconnected. No automation. No reproducibility. Every workflow reinvented from scratch.",
      },
      {
        type: "p",
        text: "We built Rayloop to fix that.",
      },
      {
        type: "h2",
        text: "What Rayloop Does",
      },
      {
        type: "p",
        text: "Rayloop is a visual workflow builder for AI content pipelines. You drag nodes onto a canvas, connect them with edges, and each edge defines how the output of one AI model becomes the input of the next.",
      },
      {
        type: "p",
        text: "A simple pipeline might look like this: a Text Input node feeds into an LLM Text node (using Gemini, Claude, or GPT-4) which produces a script, which flows into a Video Gen node to render the visuals, which connects to an Audio TTS node for voiceover. That's four nodes, three connections, and a complete short-form video pipeline — built in under five minutes.",
      },
      {
        type: "h2",
        text: "Why Visual?",
      },
      {
        type: "p",
        text: "We considered building a YAML-based pipeline DSL. Developers love YAML. But the people who need this most — creators, marketers, small teams — don't. They need something they can see and modify without reading documentation.",
      },
      {
        type: "p",
        text: "The canvas model also makes debugging intuitive. When a pipeline stalls, you see exactly which node failed and what it produced. There's no log-scanning, no stack traces. Just a visual state machine you can inspect in seconds.",
      },
      {
        type: "h2",
        text: "What You Can Build",
      },
      {
        type: "p",
        text: "Rayloop ships with 12+ node types across five categories:",
      },
      {
        type: "ul",
        items: [
          "Input nodes: Text Input, File Input, Image Input",
          "Generation nodes: LLM Text, Image Gen, Video Gen, Audio TTS, Audio Music",
          "Processing nodes: Background Remover, Image Upscaler, Video Assembly, Text Builder",
        ],
      },
      {
        type: "p",
        text: "And nine pre-built templates to get you started: YouTube Shorts, Instagram Reels, YouTube Long-Form, Product Showcase, Podcast Clip Generator, Ad Creative Generator, Brand Asset Kit, Music Video Generator, and Case Study Builder.",
      },
      {
        type: "h2",
        text: "The AI Assistant",
      },
      {
        type: "p",
        text: 'Every project also includes an AI chat assistant. Describe what you want to build in plain English — "create a video pipeline from a text prompt" — and the assistant builds the pipeline for you: adds nodes, connects them, configures models. You can treat it as a starting point and modify from there, or let it handle the whole setup.',
      },
      {
        type: "h2",
        text: "What's Next",
      },
      {
        type: "p",
        text: "We're working on scheduling (run pipelines on a cron), team workspaces, and an API so pipelines can be triggered from external tools. If you're building content workflows and want to move faster, we'd love to have you try it.",
      },
    ],
  },
  {
    slug: "youtube-shorts-pipeline-in-5-minutes",
    title: "From Prompt to Published: Build a YouTube Shorts in 5 Minutes",
    excerpt:
      "YouTube Shorts is one of the highest-leverage formats right now. The bottleneck isn't ideas — it's the assembly line. Here's how to automate it with a six-node Rayloop pipeline.",
    category: "Tutorial",
    date: "March 2, 2026",
    readTime: "8 min read",
    gradient: "from-violet-500/20 to-purple-600/10",
    content: [
      {
        type: "p",
        text: "YouTube Shorts is one of the highest-leverage content formats right now — short enough to produce fast, high enough reach to matter. The bottleneck isn't ideas. It's the assembly line: write the script, generate visuals, record or synthesize voiceover, assemble the clip. Done manually, that's 2–4 hours per short.",
      },
      {
        type: "p",
        text: "With a Rayloop pipeline, once you've set it up once, you run it in seconds. Here's how to build it.",
      },
      {
        type: "h2",
        text: "The Pipeline You're Building",
      },
      {
        type: "p",
        text: "Each node in your pipeline passes its output to the next. The final result is a video clip with synthesized voiceover, ready to upload.",
      },
      {
        type: "h2",
        text: "Step 1: Add a Text Input Node",
      },
      {
        type: "p",
        text: "Start with a Text Input node. This is where you'll enter the topic for each Short — for example, \"3 reasons to drink more water\". The node's output is that raw text, which flows into the next step.",
      },
      {
        type: "h2",
        text: "Step 2: Generate the Script",
      },
      {
        type: "p",
        text: "Connect an LLM Text node. In its system prompt, write something like:",
      },
      {
        type: "callout",
        text: "You are writing a YouTube Shorts script. Given a topic, write a 45–60 second script in a punchy, fast-paced style with 3 clear points. Keep each sentence short.",
      },
      {
        type: "p",
        text: "Connect the Text Input's output to this node's user message. Pick your model — Gemini 3 Flash is fast and cost-effective for this.",
      },
      {
        type: "h2",
        text: "Step 3: Generate Scene Prompts",
      },
      {
        type: "p",
        text: "Add a second LLM Text node. This one takes the script and breaks it into image generation prompts — one per scene. Prompt it:",
      },
      {
        type: "callout",
        text: "Given this YouTube Shorts script, extract 4–5 key scenes. For each scene, write a single, vivid image generation prompt in the format: [subject], [setting], [lighting], [style]. Output only the prompts, one per line.",
      },
      {
        type: "h2",
        text: "Step 4: Generate Images",
      },
      {
        type: "p",
        text: "Connect an Image Gen node to the prompts output. Select your image model like Nano Banana 2. The node will generate one image per prompt in the batch.",
      },
      {
        type: "h2",
        text: "Step 5: Assemble the Video",
      },
      {
        type: "p",
        text: "Add a Video Gen node. Configure it with a 9:16 aspect ratio for Shorts, your preferred frame rate, and transition style. The node takes the generated images and assembles them into a clip.",
      },
      {
        type: "h2",
        text: "Step 6: Add Voiceover",
      },
      {
        type: "p",
        text: "Finally, connect an Audio TTS node. Feed it the script from Step 2. Pick a voice, and set the speed slightly above normal (1.1x) to match the Shorts pacing.",
      },
      {
        type: "h2",
        text: "Run It",
      },
      {
        type: "p",
        text: "Hit Run in the top bar. Rayloop executes the pipeline in order, streaming live status updates as each node completes. When it finishes, you'll see the generated video and audio in the outputs panel on the right.",
      },
      {
        type: "p",
        text: "The whole setup takes about five minutes. After that, each new Short is just a new topic string and a click.",
      },
    ],
  },
];

export function getPostBySlug(slug: string): BlogPost | undefined {
  return blogPosts.find((p) => p.slug === slug);
}
